import { localeMeta } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { Puzzle } from "lucide-react";

import { AddonCard } from "@/components/site/AddonCard";
import { EmptyState, PageHero } from "@/components/site/ContentViews";
import { ConsultCta } from "@/components/site/Cta";
import { IsoGrid } from "@/components/site/Iso";
import { Section } from "@/components/site/Section";
import { ISO } from "@/content/illustrations";
import { getAddons, getPricing } from "@/lib/pricing.functions";
import { isMeteredFeature } from "@/lib/pricing-categories";
import { alternateLinks, normalizeLocale } from "@/lib/i18n";
import { useTx } from "@/lib/i18n-dict";

const TITLE = "Add-ons — extend factory.online with independent modules";
const DESCRIPTION =
  "CRM, WMS, PDM, CablePLM, Project Management, Services, S&OP and AI Agents: independent add-ons you can attach to your factory.online plan, each priced separately.";

export const Route = createFileRoute("/{-$locale}/addons/")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const [addons, pricing] = await Promise.all([
      getAddons({ data: { locale } }),
      getPricing({ data: { locale } }),
    ]);
    return {
      addons: addons.filter((addon) => !isMeteredFeature(addon)),
      plans: pricing.plans,
    };
  },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/addons"),
  });
  },
  component: AddonsHub,
});

function AddonsHub() {
  const { addons: rawAddons, plans: rawPlans } = Route.useLoaderData();
  const addons = useTx(rawAddons);
  const plans = useTx(rawPlans);
  const planName = new Map(plans.map((p) => [p.id, p.name]));

  return (
    <>
      <PageHero
        eyebrow="Add-ons"
        title="Extend your plan with independent modules"
        lead="Each add-on is a product in its own right — attach the ones your operation needs, pay only for those."
      />
      <Section className="relative overflow-hidden">
        <IsoGrid intensity="soft" />
        <img
          src={ISO.kanban}
          alt=""
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute -right-24 -top-16 -z-10 hidden w-[520px] opacity-[0.07] lg:block"
        />
        {addons.length ? (
          <div className="flex flex-col gap-6">
            {addons.map((addon, index) => (
              <AddonCard
                key={addon.id}
                addon={addon}
                delay={index * 40}
                stepsPreview
                wide
                planNames={addon.planIds
                  .map((id) => planName.get(id))
                  .filter((n): n is string => Boolean(n))}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Puzzle />}
            title="No add-ons published yet"
            hint="Add-ons will appear here as soon as they are published."
          />
        )}
      </Section>
      <ConsultCta
        title="Add-ons pay for themselves — start with the one that hurts most"
        body="Tell us where production hurts today. In one live session we show which add-on pays off first and how it plugs into the modules you already run."
        bullets={[
          "Live walkthrough of the add-ons you care about",
          "Per-user cost estimate for your team",
          "Integration plan with your current systems",
        ]}
      />
    </>
  );
}
